import { RADII, SPACING, STROKES } from "../../../lib/brand";
import { PANE_THEMES } from "../../../components/brand/paneThemes";
import type { Href } from "expo-router";
import { router, useFocusEffect } from "expo-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ScrollView, Text, View } from "react-native";

import {
  createCompany,
  listAllCompanies,
  listAllCompetitions,
  type AdminCompanyListRow,
} from "../../../lib/admin";
import { exportRowsAsCsv, todayDateForFilename } from "../../../lib/csvExport";
import { useAdminListFilter } from "../../../lib/useAdminListFilter";
import {
  AdminBanner,
  AdminButton,
  AdminEmpty,
  AdminInput,
  AdminLoading,
  AdminModal,
  AdminPageHeader,
  FilterChip,
  ListRow,
  ListRowGroup,
} from "../../../components/admin/ui";

const THEME = PANE_THEMES.Admin;
const INDUSTRY_CAP = 12;

export default function AdminClientsScreen() {
  const [rows, setRows] = useState<AdminCompanyListRow[]>([]);
  const [activeCompanyIds, setActiveCompanyIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [newOpen, setNewOpen] = useState(false);
  const [showAllIndustries, setShowAllIndustries] = useState(false);
  const hasLoadedRef = useRef(false);

  const filter = useAdminListFilter({
    keys: ["q", "engagement", "industry", "activity", "sort"],
    defaults: { q: "", engagement: "all", industry: "all", activity: "all", sort: "alpha" },
  });
  const search = filter.get("q");
  const engagementVal = filter.get("engagement");
  const industryVal = filter.get("industry");
  const activityVal = filter.get("activity");
  const sortVal = filter.get("sort");

  const [searchInput, setSearchInput] = useState(search);
  useEffect(() => setSearchInput(search), [search]);
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== search) filter.set({ q: searchInput });
    }, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const load = useCallback(async () => {
    if (!hasLoadedRef.current) setLoading(true);
    try {
      const [companies, comps] = await Promise.all([listAllCompanies(), listAllCompetitions()]);
      setRows(companies);
      const live = new Set<string>();
      for (const c of comps) {
        if (c.status === "open" || c.status === "judging") live.add(c.company_id);
      }
      setActiveCompanyIds(live);
    } finally {
      hasLoadedRef.current = true;
      setLoading(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load])
  );

  const industries = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) if (r.industry?.trim()) set.add(r.industry.trim());
    return [...set].sort();
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = rows.filter((r) => {
      if (engagementVal !== "all" && r.engagement_model !== engagementVal) return false;
      if (industryVal !== "all" && r.industry !== industryVal) return false;
      if (activityVal === "live" && !activeCompanyIds.has(r.id)) return false;
      if (activityVal === "dormant" && activeCompanyIds.has(r.id)) return false;
      if (q && !r.name.toLowerCase().includes(q)) return false;
      return true;
    });
    list = [...list].sort((a, b) => {
      if (sortVal === "competitions") return b.competition_count - a.competition_count;
      return a.name.localeCompare(b.name);
    });
    return list;
  }, [rows, search, engagementVal, industryVal, activityVal, sortVal, activeCompanyIds]);

  const visibleIndustries = showAllIndustries ? industries : industries.slice(0, INDUSTRY_CAP);

  return (
    <ScrollView style={{ flex: 1, backgroundColor: THEME.pageBg }} contentContainerStyle={{ paddingBottom: 32 }}>
      <View style={{ padding: SPACING.lg, maxWidth: 1080 }}>
        <AdminPageHeader
          theme={THEME}
          eyebrow="People · Clients"
          title="All companies"
          subtitle="Existing clients and cold companies created during discovery calls."
          actions={
            <>
              <AdminButton
                theme={THEME}
                label="Export CSV"
                variant="ghost"
                size="sm"
                onPress={() =>
                  exportRowsAsCsv({
                    filename: `companies_${todayDateForFilename()}.csv`,
                    auditPage: "clients",
                    columns: [
                      { header: "Company", get: (r: AdminCompanyListRow) => r.name },
                      { header: "Industry", get: (r: AdminCompanyListRow) => r.industry ?? "" },
                      { header: "Size", get: (r: AdminCompanyListRow) => r.size ?? "" },
                      { header: "Engagement", get: (r: AdminCompanyListRow) => r.engagement_model ?? "" },
                      { header: "Users", get: (r: AdminCompanyListRow) => r.user_count },
                      { header: "Competitions", get: (r: AdminCompanyListRow) => r.competition_count },
                    ],
                    rows: filtered,
                  })
                }
              />
              <AdminButton theme={THEME} label="+ New company" onPress={() => setNewOpen(true)} />
            </>
          }
        />

        {/* Filter bar */}
        <View style={{ padding: 12, borderRadius: RADII.md, backgroundColor: THEME.surfaceRaisedBg, borderWidth: STROKES.hairline, borderColor: THEME.border, gap: 10 }}>
          <AdminInput theme={THEME} value={searchInput} onChangeText={setSearchInput} placeholder="Search company name…" />
          <FilterRow label="Engagement">
            <FilterChip theme={THEME} label="All" active={engagementVal === "all"} onPress={() => filter.set({ engagement: "all" })} />
            <FilterChip theme={THEME} label="Coravu Managed" active={engagementVal === "rm"} onPress={() => filter.set({ engagement: "rm" })} />
            <FilterChip theme={THEME} label="Self-Managed" active={engagementVal === "licensing"} onPress={() => filter.set({ engagement: "licensing" })} />
          </FilterRow>
          {industries.length > 0 ? (
            <FilterRow label="Industry">
              <FilterChip theme={THEME} label="All" active={industryVal === "all"} onPress={() => filter.set({ industry: "all" })} />
              {visibleIndustries.map((ind) => (
                <FilterChip key={ind} theme={THEME} label={ind} active={industryVal === ind} onPress={() => filter.set({ industry: ind })} />
              ))}
              {industries.length > INDUSTRY_CAP ? (
                <FilterChip theme={THEME} label={showAllIndustries ? "Show fewer" : `+${industries.length - INDUSTRY_CAP} more`} active={false} onPress={() => setShowAllIndustries((s) => !s)} />
              ) : null}
            </FilterRow>
          ) : null}
          <FilterRow label="Activity">
            <FilterChip theme={THEME} label="Any" active={activityVal === "all"} onPress={() => filter.set({ activity: "all" })} />
            <FilterChip theme={THEME} label="Has open competition" active={activityVal === "live"} onPress={() => filter.set({ activity: "live" })} />
            <FilterChip theme={THEME} label="No active comps" active={activityVal === "dormant"} onPress={() => filter.set({ activity: "dormant" })} />
          </FilterRow>
          <FilterRow label="Sort">
            <FilterChip theme={THEME} label="Alphabetical" active={sortVal === "alpha"} onPress={() => filter.set({ sort: "alpha" })} />
            <FilterChip theme={THEME} label="Most competitions" active={sortVal === "competitions"} onPress={() => filter.set({ sort: "competitions" })} />
          </FilterRow>
        </View>

        <View style={{ marginTop: SPACING.md }}>
          {loading ? (
            <AdminLoading theme={THEME} label="Loading companies…" />
          ) : filtered.length === 0 ? (
            <AdminEmpty
              theme={THEME}
              title={rows.length === 0 ? "No companies yet." : "No companies match these filters."}
              body={rows.length === 0 ? 'Use "+ New company" when a discovery call comes in cold.' : undefined}
            />
          ) : (
            <ListRowGroup theme={THEME}>
              {filtered.map((row, i) => (
                <ListRow
                  key={row.id}
                  theme={THEME}
                  first={i === 0}
                  onPress={() => router.push(`/admin/company/${row.id}` as Href)}
                  title={row.name}
                  subtitle={
                    <View>
                      <Text style={{ fontFamily: THEME.bodyFont, fontSize: 11.5, color: THEME.textOnSurfaceDim }} numberOfLines={1}>
                        {row.size ?? "—"} · {row.industry ?? "—"} · {row.engagement_model ?? "—"}
                      </Text>
                      <Text style={{ marginTop: 1, fontFamily: THEME.bodyFont, fontSize: 11, color: THEME.textOnSurfaceFaint }} numberOfLines={1}>
                        {row.user_count} user{row.user_count === 1 ? "" : "s"} · {row.competition_count} competition{row.competition_count === 1 ? "" : "s"}
                      </Text>
                    </View>
                  }
                />
              ))}
            </ListRowGroup>
          )}
        </View>
      </View>

      {newOpen ? (
        <NewCompanyModal
          onClose={() => setNewOpen(false)}
          onCreated={(id) => {
            setNewOpen(false);
            void load();
            router.push(`/admin/company/${id}` as Href);
          }}
        />
      ) : null}
    </ScrollView>
  );
}

function FilterRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
      <Text style={{ fontFamily: THEME.bodyMediumFont, fontSize: 11, color: THEME.textOnSurfaceDim }}>{label}:</Text>
      {children}
    </View>
  );
}

function NewCompanyModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [name, setName] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [industry, setIndustry] = useState("");
  const [size, setSize] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function onSave() {
    setErr(null);
    if (!name.trim()) {
      setErr("Company name is required.");
      return;
    }
    setBusy(true);
    const { id, error } = await createCompany({
      name,
      contact_name: contactName.trim() || null,
      contact_email: contactEmail.trim() || null,
      industry: industry.trim() || null,
      size: size.trim() || null,
    });
    setBusy(false);
    if (error || !id) {
      setErr(error?.message ?? "Could not create the company.");
      return;
    }
    onCreated(id);
  }

  return (
    <AdminModal
      theme={THEME}
      visible
      onClose={onClose}
      maxWidth={520}
      title="New company"
      subtitle="For mid-call use — the client doesn't need an account yet. Send them an invite from the company page when you're ready."
      footer={
        <>
          <AdminButton theme={THEME} label="Cancel" variant="ghost" onPress={onClose} />
          <AdminButton theme={THEME} label="Create" loading={busy} onPress={() => void onSave()} />
        </>
      }
    >
      <View style={{ gap: 12 }}>
        <AdminInput theme={THEME} label="Company name *" value={name} onChangeText={setName} placeholder="Acme Corp" />
        <AdminInput theme={THEME} label="Contact name" value={contactName} onChangeText={setContactName} placeholder="Person you spoke with" />
        <AdminInput theme={THEME} label="Contact email" value={contactEmail} onChangeText={setContactEmail} placeholder="contact@company.com" autoCapitalize="none" keyboardType="email-address" />
        <AdminInput theme={THEME} label="Industry" value={industry} onChangeText={setIndustry} placeholder="Optional" />
        <AdminInput theme={THEME} label="Company size" value={size} onChangeText={setSize} placeholder="e.g. 50-200" />
        {err ? <AdminBanner theme={THEME} tone="error" message={err} /> : null}
      </View>
    </AdminModal>
  );
}
